import React, { useRef, useState } from 'react';
import { Box, Button, FormHelperText, Typography } from '../ui';

interface FileDropzoneProps {
  accept?: string;
  value?: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  label?: string;
  disabled?: boolean;
  existingFileName?: string;
  existingFileUrl?: string;
  [key: string]: any;
}

const FileDropzone: React.FC<FileDropzoneProps> = ({
  accept = '*',
  value,
  onChange,
  error,
  label,
  disabled = false,
  existingFileName,
  existingFileUrl,
  ...props
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Validate file type based on accept prop
  const validateFileType = (file: File): boolean => {
    if (accept === '*') return true;

    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    // Parse accept string: can contain extensions (.ppt) and MIME types (application/vnd.ms-powerpoint)
    const acceptParts = accept.toLowerCase().split(',').map(s => s.trim());

    // Check if file matches any accepted type
    for (const acceptPart of acceptParts) {
      if (acceptPart.startsWith('.')) {
        // Extension check
        if (fileName.endsWith(acceptPart)) {
          return true;
        }
      } else {
        // MIME type check
        if (fileType === acceptPart) {
          return true;
        }
      }
    }

    return false;
  };

  // Get user-friendly error message based on accept prop
  const getFileTypeErrorMessage = (): string => {
    if (accept === '*') return 'Invalid file type';

    const extensions = accept.toLowerCase().split(',')
      .map(s => s.trim())
      .filter(s => s.startsWith('.'))
      .join(', ');

    return `Please select a valid ${label || 'file'} (${extensions})`;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type
      if (!validateFileType(file)) {
        setValidationError(getFileTypeErrorMessage());
        return;
      }

      setValidationError('');
      onChange(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type (additional check for robustness)
      if (!validateFileType(file)) {
        setValidationError(getFileTypeErrorMessage());
        if (fileInputRef.current) {
          fileInputRef.current.value = ''; // Clear the input
        }
        return;
      }

      setValidationError('');
      onChange(file);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValidationError('');
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Box {...props}>
      <Box
        sx={{
          border: '2px dashed',
          borderColor: (error || validationError) ? 'error.main' : isDragging ? 'primary.main' : 'grey.400',
          borderRadius: 1,
          bgcolor: (error || validationError) ? 'error.light' : isDragging ? 'primary.light' : 'grey.50',
          p: 3,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          minHeight: '120px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.6 : 1,
          '&:hover': {
            borderColor: disabled ? 'grey.400' : (error || validationError) ? 'error.dark' : 'primary.main',
            bgcolor: disabled ? 'grey.50' : (error || validationError) ? 'error.light' : 'primary.50',
          },
        }}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          hidden
          accept={accept}
          onChange={handleFileChange}
          disabled={disabled}
        />

        {value ? (
          // Show newly selected file
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                📄 New file: {value.name}
              </Typography>
              <Button
                size="small"
                variant="text"
                color="error"
                onClick={handleRemove}
                disabled={disabled}
                sx={{ minWidth: 'auto', p: 0.5 }}
              >
                ✕
              </Button>
            </Box>
            {existingFileName && (
              <Typography variant="caption" color="text.disabled">
                (will replace existing file)
              </Typography>
            )}
          </Box>
        ) : existingFileName ? (
          // Show existing file with option to replace
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              ✓ {label ? `${label} already uploaded` : 'File already uploaded'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isDragging ? `Drop new ${label || 'file'} to replace` : `Click or drag to upload a new ${label || 'file'} to replace it (optional)`}
            </Typography>
          </Box>
        ) : (
          // Show empty state for new upload
          <Box>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {isDragging ? `Drop ${label || 'file'} here` : `Drag & drop ${label || 'file'} here`}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              or click to browse
            </Typography>
          </Box>
        )}
      </Box>

      {(error || validationError) && (
        <FormHelperText error sx={{ mt: 1 }}>
          {error || validationError}
        </FormHelperText>
      )}
    </Box>
  );
};

export default FileDropzone;
